// frontend/src/hooks/useApi.js
import { useState, useEffect, useCallback } from 'react';

export function useApi(apiFn, params = {}, enabled = true) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetch = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await apiFn(params);
      setData(result);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), enabled]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

/**
 * useMetaData — loads months + filter options once after login.
 * Exposes separate app lists for omni views vs social view.
 */
export function useMetaData() {
  const [months,   setMonths]   = useState([]);
  const [filters,  setFilters]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // dynamic import avoids circular dep issues
        const { Meta } = await import('../utils/api');
        const [monthsData, filtersData] = await Promise.all([
          Meta.months(),
          Meta.filters(),
        ]);
        if (!cancelled) {
          setMonths((monthsData || []).map(r => r.month_id));
          setFilters(filtersData?.[0] || filtersData || {});
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const monthList = months;
  const latestMonth = monthList[monthList.length - 1] || '';
  const f = filters || {};

  return {
    months:             monthList,
    latestMonth,
    // Omni views — real apps only (Any App excluded server-side)
    categories:         ['ALL', ...(f.categories || [])],
    sessionTypes:       f.session_types || [],
    applications:       f.applications  || [],
    // Social view — separate app list with ALL Universe
    socialApplications: f.social_applications || ['ALL Universe'],
    loading,
  };
}